<ng-container *workspaceView="'full'">
<section class="br-card br-card--dense workspace-page--full customer-workspace-page">
  <header class="br-card-header workspace-collection-header">
    <h1 class="workspace-page__title">Customers</h1>
    <button *ngIf="auth.hasPermission('Customer.Manage')"
            type="button"
            class="br-btn br-btn--primary br-btn--sm workspace-collection-add"
            (click)="addCustomer()">
      <br-icon>add</br-icon> Add Customer
    </button>
  </header>

  <div class="br-card-body workspace-page__body workspace-page__body--contained">
    <!-- The picker key changed when the table grew from eight columns to every
         business field. What the picker stores is the HIDDEN set, so under the
         old key each new column would open visible for anyone who had ever
         touched the picker - twenty-seven at once. A new key starts everyone
         on the opening set; the old key held only eight columns of choice. -->
    <app-data-table class="workspace-page__panel"
        [selectionBinding]="selectionBinding"
        [columns]="columns"
        columnPickerKey="customers-columns"
        [defaultHiddenColumns]="defaultHiddenColumns"
        [resizableColumns]="true"
        [filters]="filterValues"
        [fetch]="fetch"
        [fetchChildren]="selectedIds === null ? fetchChildren : null"
        [selectable]="true"
        [searchable]="false"
        [editable]="auth.hasPermission('Customer.Manage')"
        [rowActions]="rowActionsTpl"
        emptyText="No customers found."
        (selectionChange)="onSelectionChange($event)"
        (edit)="editCustomer($event)">

      <app-data-filter dt-filters #filter
                       [fields]="filterFields"
                       [values]="filterValues"
                       (change)="filter.apply()"
                       (applied)="applyFilters($event)">
      </app-data-filter>

      <!-- One item handed to the command band, so the band spaces the group
           and the group spaces its own - the same arrangement the Leads
           table's actions use, down to the order. -->
      <div dt-actions class="br-table-actions">
        <button type="button" class="br-btn br-btn--outline-secondary br-btn--sm"
                [disabled]="exporting || !listPageCount" (click)="openExport()">
          <br-icon>download</br-icon>
          {{ exporting ? 'Reading…' : 'Export' }}
        </button>
        <!-- The shared pair, gated the way this table's server gates Import. -->
        <app-table-transfer-actions *ngIf="selectedIds === null" table="customers" title="customers"
                                    (changed)="onTransferChanged()"></app-table-transfer-actions>
        <button type="button"
                class="br-btn br-btn--outline-secondary br-btn--sm br-icon-btn"
                *ngIf="selectedIds === null && auth.hasPermission('Company.Manage')"
                [brMenuTriggerFor]="customerMoreActions"
                aria-label="Customer data actions"
                [disabled]="deletingAll">
          <br-icon>more_horiz</br-icon>
        </button>
        <ng-template #customerMoreActions>
          <br-menu aria-label="Customer data actions">
            <button brMenuItem (triggered)="deleteAllCustomers()" label="Delete All" icon="delete_sweep"></button>
          </br-menu>
        </ng-template>
      </div>
      <div dt-actions class="br-table-export-error" *ngIf="exportError">
        <br-icon>block</br-icon> {{ exportError }}
      </div>

      <!-- Shown by the table only while rows are selected. -->
      <button dt-bulk-actions class="br-btn br-btn--outline-danger br-btn--sm"
              *ngIf="auth.hasPermission('Customer.Manage')" (click)="deactivateSelected()">
        <br-icon>block</br-icon> Deactivate
      </button>


      <ng-template dtCell="id" let-row>
        <span class="br-cell-id">{{ row.id }}</span>
      </ng-template>

      <!-- Name (+ Master chip; sub-accounts open from the master's detail page)

           The name IS the way in, as the lead's title is on the Leads list.
           It was inert text, so opening a customer meant finding the eye icon
           at the far right of the row - past every column, in the one place
           the eye is not while it is reading the name. The row's own actions
           stay where they are; this is the obvious way, not the only one. -->
      <ng-template dtCell="displayName" let-row>
        <button type="button" class="cx-open-btn br-cell-primary"
                (click)="goToCustomer(row.id)"
                [attr.aria-label]="'Open ' + row.displayName">{{ row.displayName }}</button>
        <mat-chip *ngIf="row.childCount > 0" class="chip-blue customer-master-chip" disableRipple>
          Master
        </mat-chip>
      </ng-template>

      <!-- Assignee (current handler; email-style username shown as its local part) -->
      <ng-template dtCell="assignee" let-row>
        <span *ngIf="row.assigneeName">{{ row.assigneeName.split('@')[0] }}</span>
        <span *ngIf="!row.assigneeName" class="br-cell-empty">—</span>
      </ng-template>

      <!-- Type: a closed catalogue, so it reads as a chip rather than free text -->
      <ng-template dtCell="customerTypeName" let-row>
        <span *ngIf="row.customerTypeName" class="br-cell-pill">{{ row.customerTypeName }}</span>
        <span *ngIf="!row.customerTypeName" class="br-cell-empty">—</span>
      </ng-template>

      <!-- Next follow-up -->
      <ng-template dtCell="nextFollowUp" let-row>
        <span *ngIf="row.nextFollowUp">{{ row.nextFollowUp | date:'MMM d' }}</span>
        <span *ngIf="!row.nextFollowUp" class="br-cell-empty">—</span>
      </ng-template>

      <!-- Last activity -->
      <ng-template dtCell="lastActivity" let-row>
        <span *ngIf="row.lastActivityAt">{{ row.lastActivityAt | date:'MMM d' }}</span>
        <span *ngIf="!row.lastActivityAt" class="br-cell-empty">—</span>
      </ng-template>

      <!-- Created -->
      <ng-template dtCell="createdAt" let-row>
        {{ row.createdAt | date:'MMM d, y' }}
      </ng-template>

      <!-- Updated -->
      <ng-template dtCell="updatedAt" let-row>
        <span *ngIf="row.updatedAt">{{ row.updatedAt | date:'MMM d, y' }}</span>
        <span *ngIf="!row.updatedAt" class="br-cell-empty">—</span>
      </ng-template>

      <!-- Catalogue columns: a closed list, so each reads as a chip like Type.
           The row carries the name; the filter picks by id. -->
      <ng-template dtCell="billingStatus" let-row>
        <span *ngIf="row.billingStatusName" class="br-cell-pill">{{ row.billingStatusName }}</span>
        <span *ngIf="!row.billingStatusName" class="br-cell-empty">—</span>
      </ng-template>

      <ng-template dtCell="paymentTerms" let-row>
        <span *ngIf="row.paymentTermsName" class="br-cell-pill">{{ row.paymentTermsName }}</span>
        <span *ngIf="!row.paymentTermsName" class="br-cell-empty">—</span>
      </ng-template>

      <ng-template dtCell="taxArea" let-row>
        <span *ngIf="row.taxAreaName" class="br-cell-pill">{{ row.taxAreaName }}</span>
        <span *ngIf="!row.taxAreaName" class="br-cell-empty">—</span>
      </ng-template>

      <ng-template dtCell="pricingArea" let-row>
        <span *ngIf="row.pricingAreaName" class="br-cell-pill">{{ row.pricingAreaName }}</span>
        <span *ngIf="!row.pricingAreaName" class="br-cell-empty">—</span>
      </ng-template>

      <ng-template dtCell="commPref" let-row>
        <span *ngIf="row.commPrefName" class="br-cell-pill">{{ row.commPrefName }}</span>
        <span *ngIf="!row.commPrefName" class="br-cell-empty">—</span>
      </ng-template>

      <!-- Tax exempt -->
      <ng-template dtCell="isTaxExempt" let-row>{{ row.isTaxExempt ? 'Yes' : 'No' }}</ng-template>

    </app-data-table>

    <!-- Per-row actions menu, rendered by the table in its trailing column. -->
    <ng-template #rowActionsTpl let-row>
      <button mat-icon-button (click)="goToCustomer(row.id)"
              matTooltip="View" aria-label="View customer">
        <br-icon>visibility</br-icon>
      </button>
      <button mat-icon-button [brMenuTriggerFor]="rowMenu"
              matTooltip="Actions" aria-label="Row actions">
        <br-icon>more_vert</br-icon>
      </button>
      <ng-template #rowMenu>
        <br-menu aria-label="Customer actions">
          <button brMenuItem *ngIf="auth.hasPermission('Customer.Manage') && canAddChild(row)"
                  label="Add child" icon="account_tree" (triggered)="openAddChildDialog(row)"></button>
          <button brMenuItem *ngIf="auth.hasPermission('Customer.Manage')"
                  label="Deactivate" icon="block" (triggered)="onDelete(row)"></button>
        </br-menu>
      </ng-template>
    </ng-template>
  </div>
</section>
</ng-container>

<ng-container *workspaceView="'compact'">
  <section class="workspace-page workspace-page--full">
    <h1 class="workspace-page__title">Customers</h1>

    <app-data-list [searchable]="false" #compactList [fetch]="fetchCompact" [filters]="filterValues"
                   [selectable]="selectionBinding !== null" [selectionBinding]="selectionBinding"
                   [trackByFn]="trackCustomer"
                   [sortOptions]="compactSortOptions"
                   (revealMissed)="onCompactRevealMissed()">
      <app-data-filter list-filters #compactFilter [fields]="filterFields" [values]="filterValues"
                       (change)="compactFilter.apply()" (applied)="applyFilters($event)"></app-data-filter>
      <!-- The map asked for a customer these rules were hiding. -->
      <span *ngIf="revealNotice" list-filters class="br-text-muted" role="status">
        {{ revealNotice }}
        <button *ngIf="canRestoreRevealFilters" type="button"
                class="br-btn br-btn--link br-btn--sm"
                (click)="restoreRevealedFilters()">Undo</button>
      </span>
      <ng-template dataListItem let-row>
        <button type="button" class="map-locate-name"
                [appShowOnMap]="{ featureKey: 'customers', objectId: row.id }"
                [latitude]="row.lat" [longitude]="row.lng"
                [popup]="{ type: 'Customer', title: row.displayName, subtitle: row.address || undefined }"
                #nameOnMap="showOnMap" [title]="nameOnMap.located ? 'Show on map' : ''">
          <strong>{{ row.displayName }}</strong>
        </button>
        <div class="br-flex br-items-center br-gap-1 br-text-muted">
          <app-map-pin [latitude]="row.lat" [longitude]="row.lng"
                       [object]="{ featureKey: 'customers', objectId: row.id }"
                       [popup]="{ type: 'Customer', title: row.displayName, subtitle: row.address || undefined }"></app-map-pin>
          <span>{{ row.address || 'No address recorded' }}</span>
        </div>
        <div *ngIf="row.contact || row.primaryContact || row.phone || row.mobilePhone" class="br-flex br-items-center br-gap-2">
          <span *ngIf="row.contact || row.primaryContact">{{ row.contact || row.primaryContact }}</span>
          <a *ngIf="row.phone || row.mobilePhone as phone" [href]="'tel:' + phone">{{ phone }}</a>
          <span *ngIf="row.phone && row.ext" class="br-text-muted">ext. {{ row.ext }}</span>
        </div>
        <p class="br-flex br-items-center br-gap-2 br-text-muted br-mt-2 br-mb-0">
          <br-icon aria-hidden="true">auto_awesome</br-icon>
          <span>AI summary · Coming soon</span>
        </p>
      </ng-template>
      <ng-template dataListDetail let-row>
        <app-customer-page [inline]="true" [customerId]="row.id"
                             (profileLoaded)="updateCompactCustomer(row, $event)"></app-customer-page>
      </ng-template>
    </app-data-list>
  </section>
</ng-container>
